import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { isTosAccepted, highlightTos } from 'utils/tosGate';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { useKitView } from 'containers/KitViewContext';
import { completeWorkspace, getPdfUrl, getWorkspacePreview } from 'containers/KitWorkspaceStorage/api';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import { FormLoader } from 'common/components/Loader';
import BottomSheet from './BottomSheet';
import { useKitTranslate } from './useKitTranslate';
import { useKitCartCommit } from './useKitCartCommit';
import { readBrandAccent, resolveLogoUrl } from './pdfBranding';
import styles from './CombinedPdfPreview.module.scss';

// Delay highlighting the terms checkbox until the popup has closed (so it is
// scrolled into view and the body scroll-lock is released).
const TOS_HIGHLIGHT_DELAY_MS = 120;

interface ICombinedPdfPreviewProps {
  onClose: () => void;
}

/**
 * Shared "Combined PDF preview" popup. Opened from both the Manage Kits page and
 * the confirm (last builder) step. On open it folds the still-pending confirm
 * assembly into the order (while the workspace is editable) and shows the read-
 * only combined HTML from GET /preview — no PDF is generated yet. The user
 * generates from inside the popup; once the whole-workspace PDF is ready the
 * footer swaps to Download / Email / Add to cart. The workspace stays
 * `completed` (data intact), so Add to cart still has assemblies to send.
 */
const CombinedPdfPreview = ({ onClose }: ICombinedPdfPreviewProps) => {
  const t = useKitTranslate();
  const {
    workspaceKey, workspace, totals, refetch, isCompleting,
  } = useKitWorkspace();
  const { commitPending, openManageKits, closeManageKits } = useKitView();
  const {
    cartEnabled, cartLabel, onAddToCart, isSendingCart,
  } = useKitCartCommit();

  const logoUrl = resolveLogoUrl(t('variables.pdf.logo'));

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(true);
  const [isStarting, setIsStarting] = useState(false);

  const status = workspace?.status;
  const completed = status === 'completed';
  const failed = status === 'failed';
  // Treat the gap between "completed" and the presigned URL arriving as still
  // busy, so the footer never flashes back to "Finish and Generate PDF".
  const generating = isStarting || isCompleting || (completed && !pdfUrl);
  const ready = completed && !!pdfUrl;

  // On open: commit the pending assembly (only while editable) then load the
  // read-only preview. Guarded by a ref (not a cancelled flag) so StrictMode's
  // dev double-mount does not commit the assembly twice or fire two /preview
  // requests — the cancelled flag would only block setState, not the writes.
  const preparedRef = useRef(false);
  useEffect(() => {
    if (preparedRef.current) return;
    preparedRef.current = true;
    (async () => {
      try {
        let key = workspaceKey;
        if (status !== 'completed') {
          key = (await commitPending()) ?? workspaceKey;
        }
        if (!key) { setIsPreparing(false); return; }
        const { html } = await getWorkspacePreview(key, readBrandAccent(), logoUrl);
        setPreviewHtml(html);
      } catch {
        showToast(ToastTypes.error, t('texts.kits.toast.pdfError'));
      } finally {
        setIsPreparing(false);
      }
    })();
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once generation settles to completed, fetch a fresh presigned PDF URL.
  useEffect(() => {
    let cancelled = false;
    if (completed && !pdfUrl && workspaceKey) {
      getPdfUrl(workspaceKey)
        .then(({ url }) => { if (!cancelled) { setPdfUrl(url); setIsStarting(false); } })
        .catch(() => {
          if (!cancelled) { showToast(ToastTypes.error, t('texts.kits.toast.pdfError')); setIsStarting(false); }
        });
    }
    if (failed && isStarting) setIsStarting(false);
    return () => { cancelled = true; };
  }, [completed, failed, isStarting, pdfUrl, workspaceKey, t]);

  const onGenerate = useCallback(async () => {
    if (!isTosAccepted()) {
      // Close everything (preview + Manage Kits overlay) back to the builder so
      // the highlighted Accept checkbox is actually visible, not hidden behind it.
      closeManageKits();
      onClose();
      showToast(ToastTypes.warning, t('texts.kits.toast.acceptTerms'));
      window.setTimeout(highlightTos, TOS_HIGHLIGHT_DELAY_MS);
      return;
    }
    if (!workspaceKey || totals.configs === 0) {
      showToast(ToastTypes.warning, t('texts.kits.toast.nothingToGenerate'));
      return;
    }
    setIsStarting(true);
    try {
      await completeWorkspace(workspaceKey, readBrandAccent(), logoUrl);
      track('pdf_generated', { workspaceKey, kits: totals.kits, configs: totals.configs });
      // Pick up status='completing' so the workspace query starts polling.
      await refetch();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.pdfError'));
      setIsStarting(false);
    }
  }, [workspaceKey, totals, refetch, closeManageKits, onClose, t, logoUrl]);

  const handleDownload = useCallback(() => {
    if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }, [pdfUrl]);

  const handleEmail = useCallback(() => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = `mailto:${t('variables.contactEmail')}?subject=${encodeURIComponent(t('variables.contactEmailTitle'))}&body=${encodeURIComponent(pdfUrl)}`;
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.click();
  }, [pdfUrl, t]);

  // Close (button + X) fully exits: drop the preview AND the Manage Kits overlay
  // so the user lands back on the builder, never on Manage Kits. Reaching Manage
  // Kits is only via the explicit button below.
  const fullClose = useCallback(() => {
    closeManageKits();
    onClose();
  }, [closeManageKits, onClose]);

  const goToManageKits = useCallback(() => {
    openManageKits();
    onClose();
  }, [openManageKits, onClose]);

  const footer = ready ? (
    <div className={styles.footer}>
      <Button schema={ButtonSchema.secondary} size={ButtonSize.small} onClick={fullClose}>
        {t('texts.kits.preview.close')}
      </Button>
      <Button schema={ButtonSchema.primary} size={ButtonSize.small} onClick={handleEmail}>
        {t('texts.kits.preview.email')}
      </Button>
      <Button schema={ButtonSchema.primary} size={ButtonSize.small} onClick={handleDownload}>
        {t('texts.kits.preview.download')}
      </Button>
      {cartEnabled && (
        <Button
          schema={ButtonSchema.primary}
          size={ButtonSize.small}
          onClick={onAddToCart}
          disabled={isSendingCart}
        >
          {cartLabel}
        </Button>
      )}
    </div>
  ) : (
    <div className={styles.footer}>
      <Button
        schema={ButtonSchema.secondary}
        size={ButtonSize.small}
        onClick={fullClose}
        disabled={generating}
      >
        {t('texts.kits.preview.close')}
      </Button>
      <Button
        schema={ButtonSchema.primary}
        size={ButtonSize.small}
        onClick={onGenerate}
        disabled={generating || isPreparing || totals.configs === 0}
      >
        {generating ? t('texts.kits.generating') : t('texts.kits.finishBtn')}
      </Button>
    </div>
  );

  return (
    <BottomSheet
      title={t('texts.kits.preview.title')}
      subtitle={t('texts.kits.preview.subtitle', {
        kits: totals.kits,
        configs: totals.configs,
        assemblies: totals.assemblyUnits,
        loose: totals.looseUnits,
      })}
      headerAction={(
        <Button
          schema={ButtonSchema.secondary}
          size={ButtonSize.small}
          className={styles.manageKitsBtn}
          onClick={goToManageKits}
        >
          {t('texts.kits.manageKits')}
        </Button>
      )}
      onClose={fullClose}
      fullScreen
      closeDisabled={generating}
      footer={footer}
    >
      {failed && !generating && (
        <p className={styles.failedNote}>{t('texts.kits.final.failed')}</p>
      )}
      {isPreparing && <FormLoader />}
      {previewHtml !== null && (
        <div className={styles.canvas}>
          <iframe
            className={styles.frame}
            srcDoc={previewHtml}
            title={t('texts.kits.preview.title')}
          />
        </div>
      )}
    </BottomSheet>
  );
};

export default CombinedPdfPreview;
